import { RedditMentionDto } from '../../domain/entities/RedditMention';

/**
 * Creates a mock RedditMentionDto with sensible defaults.
 * All fields match the current backend DTO shape.
 *
 * @param overrides - Partial object to override specific fields
 * @returns Complete RedditMentionDto with all required fields
 */
export const createMockRedditMentionDto = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto => ({
  id: '123e4567-e89b-12d3-a456-426614174000',
  redditPostId: 'abc123xyz',
  subreddit: 'seattle',
  postTitle: 'Great local businesses in Seattle',
  content: 'I really love this place, their service is excellent!',
  postPermalink: '/r/seattle/comments/abc123/great_local_businesses',
  authorUsername: 'test_redditor',
  postCreatedAt: '2025-01-01T00:00:00Z',

  // Business relationship
  businessId: '456e7890-e89b-12d3-a456-426614174001',
  businessName: 'Test Business',

  // Reddit metrics
  upvoteCount: 42,
  commentCount: 5,
  isComment: false,

  // Match confidence (CRITICAL: Added in Sprint 013)
  matchConfidenceScore: 85.0,
  matchStatus: 'Matched',

  // CRITICAL: Added in Sprint 013 - many tests missing this!
  reviewStatus: 'Pending',

  // Emotional tone analysis
  emotionalToneScore: 0.8,
  emotionalTone: 'Positive',

  // Processing status
  processingStatus: 'Processed',

  // Audit fields
  createdAt: '2025-01-01T00:00:00Z',

  ...overrides
});

/**
 * Helper: Creates an approved mention with review status
 */
export const createApprovedMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    reviewStatus: 'Approved',
    matchStatus: 'Verified',
    processingStatus: 'Processed',
    ...overrides
  });

/**
 * Helper: Creates a rejected mention with review status
 */
export const createRejectedMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    reviewStatus: 'Rejected',
    matchStatus: 'Rejected',
    businessId: null,
    businessName: null,
    ...overrides
  });

/**
 * Helper: Creates a pending mention awaiting review
 */
export const createPendingMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    reviewStatus: 'Pending',
    matchStatus: 'Matched',
    processingStatus: 'Processed',
    ...overrides
  });

/**
 * Helper: Creates a mention with high confidence
 */
export const createHighConfidenceMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    matchConfidenceScore: 95.0,
    matchStatus: 'Verified',
    reviewStatus: 'Pending',
    ...overrides
  });

/**
 * Helper: Creates a mention with low confidence
 */
export const createLowConfidenceMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    matchConfidenceScore: 45.0,
    matchStatus: 'Matched',
    reviewStatus: 'Pending',
    ...overrides
  });

/**
 * Helper: Creates a mention without a matched business
 */
export const createUnmatchedMention = (
  overrides?: Partial<RedditMentionDto>
): RedditMentionDto =>
  createMockRedditMentionDto({
    businessId: null,
    businessName: null,
    matchConfidenceScore: null,
    matchStatus: 'Unmatched',
    reviewStatus: 'Pending',
    ...overrides
  });
